import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Calculator, CheckCircle2, CreditCard, Gift, Loader2, Mail, MapPin, Phone, Plane, Plus, Trash2,
} from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money, openCheckout } from "@/lib/api";
import { useVehiclePrices } from "@/lib/useVehiclePrices";
import { SMS_CONSENT_TEXT } from "@/lib/smsConsent";
import { clearRiderSession, readRiderSession, ridePath, saveRiderSession } from "@/lib/riderScope";
import { RouteMap } from "@/components/RouteMap";
import { AddressInput } from "@/components/AddressInput";
import { RiderAuthPanel } from "@/components/RiderAuthPanel";
import { SavedPlaceChips } from "@/components/SavedPlaceChips";
import { QuoteBreakdown } from "@/components/QuoteBreakdown";
import { VehicleGallery } from "@/components/VehicleGallery";
import { InstallPrompt } from "@/components/InstallPrompt";
import { SandboxNotice } from "@/components/SandboxNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const emptyPlace = { address: "", formatted_address: "", place_id: null, lat: null, lng: null };
const field = "h-12 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold";

export default function RiderApp() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [booking, setBooking] = useState(null);
  const [quote, setQuote] = useState(null);
  const [miles, setMiles] = useState(null);
  const [error, setError] = useState("");
  const [quoteNotice, setQuoteNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [rider, setRider] = useState(null);
  const [savedPlaces, setSavedPlaces] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "", sms_consent: false, save_card: false,
    pickup: emptyPlace, dropoff: emptyPlace, stops: [],
    vehicle_id: "", pricing_mode: "mileage", hours: 3, meet_greet: false,
    trip_date: "", pickup_time: "", passengers: 1, vehicle_pref: "",
    airline: "", flight_number: "", terminal: "", notes: "",
  });

  useEffect(() => {
    api.get(`/public/company/${companyId}`).then(({ data }) => setInfo(data)).catch((e) => setError(errMsg(e)));
  }, [companyId]);

  const cid = info?.company?.id || companyId;
  // Every in-app link stays under /ride/<handle>, the scope the installed app was granted.
  const handle = info?.company?.handle || companyId;

  useEffect(() => {
    if (!cid) return;
    api.get(`/public/company/${cid}/flat-rates`).then(({ data }) => setRoutes(data.routes)).catch(() => {});
  }, [cid]);

  // Loyalty progress for whoever is booking — how many rides until the next credit.
  useEffect(() => {
    const email = form.customer_email.trim();
    if (!cid || !email.includes("@")) { setLoyalty(null); return; }
    const timer = setTimeout(() => {
      api.get(`/public/company/${cid}/loyalty`, { params: { email } })
        .then(({ data }) => setLoyalty(data.enabled ? data : null))
        .catch(() => setLoyalty(null));
    }, 600);
    return () => clearTimeout(timer);
  }, [cid, form.customer_email]);

  // A repeat booking arrives from the trips page with the last trip's details ready to go. The key
  // is the company id, which is the same however the passenger reached us (handle or raw id).
  useEffect(() => {
    if (!cid) return;
    const raw = sessionStorage.getItem(`ride_again_${cid}`) || sessionStorage.getItem(`ride_again_${companyId}`);
    if (!raw) return;
    sessionStorage.removeItem(`ride_again_${cid}`);
    sessionStorage.removeItem(`ride_again_${companyId}`);
    try {
      const last = JSON.parse(raw);
      setForm((f) => ({
        ...f,
        pickup: last.pickup || f.pickup,
        dropoff: last.dropoff || f.dropoff,
        passengers: last.passengers || f.passengers,
        vehicle_id: last.vehicle_id || f.vehicle_id,
        vehicle_pref: last.vehicle_pref || f.vehicle_pref,
      }));
    } catch (e) {
      console.warn("could not restore the repeat-booking details", e);
    }
  }, [cid]);

  // A signed-in passenger gets their details and saved addresses filled in for them. The session is
  // stored against the company id so a /ride/<handle> link and a /ride/<id> link share one sign-in.
  useEffect(() => {
    if (!cid) return;
    const session = readRiderSession([cid, handle, companyId]);
    if (!session?.token) return;
    saveRiderSession([cid, handle, companyId], session);
    setRider(session);
    setForm((f) => ({ ...f, customer_email: f.customer_email || session.email,
                      customer_name: f.customer_name || session.name || "" }));
    api.get(`/public/rider-auth/session/${session.token}/places`)
      .then(({ data }) => setSavedPlaces(data.places))
      .catch(() => { clearRiderSession([cid, handle, companyId]); setRider(null); });
  }, [cid, handle, companyId]);

  useEffect(() => {
    const name = info?.company?.name;
    if (!name) return;
    document.title = `Book a ride — ${name}`;
  }, [info]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reset = (patch) => { setForm((f) => ({ ...f, ...patch })); setQuote(null); };
  const vehicles = useMemo(() => info?.vehicles || [], [info]);
  const vehicle = vehicles.find((v) => v.id === form.vehicle_id);
  const points = useMemo(() => [form.pickup, ...form.stops, form.dropoff], [form.pickup, form.stops, form.dropoff]);
  // The map stays away until both ends are known — it used to swallow the address suggestions.
  const showMap = Boolean(form.pickup?.address && form.dropoff?.address);

  const getQuote = async () => {
    if (!form.vehicle_id) { setError("Pick a vehicle to see your price."); return; }
    setBusy("quote"); setError("");
    try {
      const { data } = await api.post(`/public/company/${cid}/quote`, {
        vehicle_id: form.vehicle_id, pricing_mode: form.pricing_mode,
        pickup: form.pickup, dropoff: form.dropoff, stops: form.stops,
        hours: Number(form.hours || 0), miles: null, meet_greet: form.meet_greet,
        customer_email: form.customer_email,
      });
      setMiles(data.miles ?? null);
      setQuote(data);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  // Fares appear on every vehicle as soon as both ends of the route are known.
  const { prices, miles: pricedMiles, loading: pricing } = useVehiclePrices({
    companyId: cid, pickup: form.pickup, dropoff: form.dropoff, stops: form.stops,
    hours: form.hours, pricingMode: form.pricing_mode, meetGreet: form.meet_greet,
    email: form.customer_email,
  });

  useEffect(() => {
    setQuote(prices[form.vehicle_id] || null);
    if (pricedMiles != null) setMiles(pricedMiles);
  }, [prices, form.vehicle_id, pricedMiles]);

  const emailQuote = async () => {
    if (!form.customer_email) { setError("Add your email above and we'll send the quote over."); return; }
    setBusy("email-quote"); setError(""); setQuoteNotice("");
    try {
      await api.post(`/public/company/${cid}/quote-email`, {
        vehicle_id: form.vehicle_id, pricing_mode: form.pricing_mode,
        pickup: form.pickup, dropoff: form.dropoff, stops: form.stops,
        hours: Number(form.hours || 0), miles: miles ?? 0, meet_greet: form.meet_greet,
        email: form.customer_email, name: form.customer_name,
        trip_date: form.trip_date, pickup_time: form.pickup_time,
        passengers: Number(form.passengers || 1),
      });
      setQuoteNotice(`Quote sent to ${form.customer_email} — book from the email whenever you're ready.`);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy("submit"); setError("");
    try {
      const { data } = await api.post(`/public/company/${cid}/bookings`, {
        ...form,
        passengers: Number(form.passengers || 1),
        hours: Number(form.hours || 0),
        miles: null,
        stops: form.stops.map(({ address, formatted_address, place_id, lat, lng }) => ({ address, formatted_address, place_id, lat, lng })),
      });
      setBooking(data);
      api.post(`/public/company/${cid}/rider-link`, { email: form.customer_email }).catch(() => {});
      // Reservation made — straight to secure payment, unless the operator confirms trips first.
      if (Number(data.deposit_due || 0) > 0 && info?.company?.online_payments_ready
          && data.pay_before_approval !== false) {
        payDeposit(data.id);
      }
    } catch (err) { setError(errMsg(err)); } finally { setBusy(""); }
  };

  const payDeposit = async (bookingId) => {
    setBusy("pay"); setError("");
    try {
      const { data } = await api.post(`/public/bookings/${bookingId || booking.id}/deposit-checkout`, { origin_url: window.location.origin });
      if (!openCheckout(data.checkout_url, setCheckoutUrl)) { setCheckoutUrl(data.checkout_url); setBusy(""); }
    } catch (e) { setError(errMsg(e)); setBusy(""); }
  };

  const signIn = (session) => {
    saveRiderSession([cid, handle, companyId], session);
    setRider(session);
    setForm((f) => ({ ...f, customer_email: f.customer_email || session.email,
                      customer_name: f.customer_name || session.name || "" }));
    api.get(`/public/rider-auth/session/${session.token}/places`)
      .then(({ data }) => setSavedPlaces(data.places))
      .catch(() => {});
  };

  const signOut = () => {
    clearRiderSession([cid, handle, companyId]);
    setRider(null);
    setSavedPlaces([]);
  };
  if (!info) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900">
        {error ? <p data-testid="rider-page-error" className="text-sm text-red-400">{error}</p> : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  const c = info.company;

  return (
    <div data-testid="rider-app" className="min-h-screen bg-ink-900 text-slate-200">
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-t-2xl bg-ink-800 p-7" style={{ borderTop: `3px solid ${c.branding?.primary_color || "#D4AF37"}` }}>
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{c.name}</p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-50">Book your ride</h1>
              {(c.contact_phone || c.phone) && (
                <a data-testid="rider-contact-phone" href={`tel:${(c.contact_phone || c.phone).replace(/[^\d+]/g, "")}`}
                  className="mt-2 flex items-center gap-2 text-xs text-slate-400 transition-colors hover:text-gold">
                  <Phone className="h-3 w-3" /> {c.contact_phone || c.phone}
                </a>
              )}
              {c.contact_hours && (
                <p data-testid="rider-contact-hours" className="mt-1 text-xs text-slate-500">{c.contact_hours}</p>
              )}
            </div>
            {c.logo_url && <img src={c.logo_url} alt={`${c.name} logo`} className="h-14 w-auto object-contain" />}
          </div>
        </div>

        <div className="rounded-b-2xl border border-t-0 border-ink-600 bg-ink-800/60 p-7">
          {booking ? (
            <div data-testid="rider-confirmation" className="space-y-5 py-4 text-center">
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
              <h2 className="text-2xl font-bold text-slate-50">Ride requested</h2>
              <p className="text-sm text-slate-400">
                {c.name} will confirm shortly. We also emailed you a private link to track your ride and add stops.
              </p>
              {booking.deposit_due > 0 && info?.company?.online_payments_ready && (
                <div className="rounded-xl border border-ink-600 bg-ink-900/60 p-5 text-left">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Secure your reservation</p>
                  <p className="mt-2 text-sm text-slate-300">
                    Estimated total {money(booking.estimated_total)} · due now{" "}
                    <span className="font-semibold text-slate-50">{money(booking.deposit_due)}</span>
                  </p>
                  <Button data-testid="rider-pay-deposit" disabled={busy === "pay"} onClick={payDeposit} className="mt-4 h-14 w-full bg-gold text-base text-ink-900 hover:bg-gold-light">
                    {busy === "pay" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CreditCard className="mr-2 h-4 w-4" /> Pay {money(booking.deposit_due)} now</>)}
                  </Button>
                  {c.payments_sandbox && (
                    <div className="mt-3">
                      <SandboxNotice testId="rider-sandbox" />
                    </div>
                  )}
                </div>
              )}
              <Button data-testid="rider-book-another" variant="outline" onClick={() => { setBooking(null); setQuote(null); }}
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                Book another ride
              </Button>
              <InstallPrompt companyName={c.name} />
              {error && <p data-testid="rider-deposit-error" className="text-sm text-red-400">{error}</p>}
              {checkoutUrl && (
                <a data-testid="rider-checkout-link" href={checkoutUrl} target="_blank" rel="noopener noreferrer"
                  className="block rounded-xl border border-gold/40 bg-gold/10 p-4 text-center text-sm font-semibold text-gold">
                  Open the secure card form →
                </a>
              )}
            </div>
          ) : (
            <form data-testid="rider-form" onSubmit={submit}
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") e.preventDefault(); }}
              className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                {[["mileage", "Point-to-point"], ["hourly", "Hourly charter"]].map(([k, label]) => (
                  <button type="button" key={k} data-testid={`rider-mode-${k}`}
                    onClick={() => reset({ pricing_mode: k })}
                    className={`rounded-xl border p-4 text-left transition-colors ${
                      form.pricing_mode === k ? "border-gold/50 bg-gold/10 text-slate-100" : "border-ink-600 bg-ink-800 text-slate-400 hover:border-gold/30"
                    }`}>
                    <p className="text-sm font-medium">{label}</p>
                  </button>
                ))}
              </div>

              {routes.length > 0 && (
                <div data-testid="rider-popular-routes" className="rounded-xl border border-ink-600 bg-ink-800/60 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Popular flat rates</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {routes.slice(0, 6).map((r) => (
                      <button type="button" key={`${r.origin}-${r.airport}`}
                        data-testid={`rider-route-${r.origin.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => reset({ pickup: { ...emptyPlace, address: r.origin, formatted_address: r.origin },
                                               dropoff: { ...emptyPlace, address: r.airport, formatted_address: r.airport } })}
                        className="rounded-full border border-ink-600 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-gold/40 hover:text-gold">
                        {r.origin} → {r.airport} <span className="ml-1 font-mono text-gold">{money(r.price)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {loyalty && (
                <div data-testid="rider-loyalty" className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                    <Gift className="h-3.5 w-3.5" /> Loyalty
                  </p>
                  <p className="mt-2 text-sm text-slate-200">
                    {loyalty.available_credit > 0
                      ? `You have ${money(loyalty.available_credit)} in ride credit — it comes off this booking automatically.`
                      : `${loyalty.rides_to_next} more ride${loyalty.rides_to_next === 1 ? "" : "s"} and you earn ${money(loyalty.credit_amount)} off your next one.`}
                  </p>
                </div>
              )}

              <AddressInput label="Pickup" testId="rider-pickup" companyId={cid} useMyLocation value={form.pickup} onChange={(p) => reset({ pickup: p })} />
              <SavedPlaceChips places={savedPlaces} testId="rider-pickup-saved" onPick={(p) => reset({ pickup: p })} />
              {form.stops.map((s, i) => (
                <div key={s.place_id || `${s.address}-${i}`} className="flex items-end gap-3">
                  <div className="flex-1">
                    <AddressInput label={`Stop ${i + 1}`} testId={`rider-stop-${i}`} companyId={cid} value={s}
                      onChange={(p) => reset({ stops: form.stops.map((x, xi) => (xi === i ? { ...x, ...p } : x)) })} />
                  </div>
                  <button type="button" data-testid={`rider-stop-remove-${i}`} className="mb-1 text-slate-500 hover:text-red-400"
                    onClick={() => reset({ stops: form.stops.filter((_, xi) => xi !== i) })}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button type="button" variant="outline" data-testid="rider-add-stop"
                onClick={() => reset({ stops: [...form.stops, { ...emptyPlace }] })}
                className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                <Plus className="mr-2 h-4 w-4" /> Add a stop
              </Button>
              <AddressInput label="Drop-off" testId="rider-dropoff" companyId={cid} value={form.dropoff} onChange={(p) => reset({ dropoff: p })} />
              <SavedPlaceChips places={savedPlaces} testId="rider-dropoff-saved" onPick={(p) => reset({ dropoff: p })} />

              {showMap && (
                <RouteMap points={points} testId="rider-map"
                  onRoute={({ miles: m }) => { if (m != null) setMiles(m); }} />
              )}

              <div>
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Vehicle</label>
                <div className="space-y-2">
                  {vehicles.map((v) => (
                    <button type="button" key={v.id} data-testid={`rider-vehicle-${v.id}`}
                      onClick={() => reset({ vehicle_id: v.id, pricing_mode: v.pricing_mode, vehicle_pref: v.name })}
                      className={`flex w-full items-center gap-4 rounded-xl border p-3 text-left transition-colors ${
                        form.vehicle_id === v.id ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/30"
                      }`}>
                      <VehicleGallery photos={v.photos} name={v.name} testId={`rider-gallery-${v.id}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-tight text-slate-100">{v.name}</p>
                        <p className="font-mono text-[11px] text-slate-500">
                          {v.capacity} pax · {v.luggage_capacity} bags
                          {v.pricing_mode === "hourly" ? ` · ${v.minimum_hours}h minimum` : ""}
                        </p>
                      </div>
                      <div data-testid={`rider-vehicle-price-${v.id}`} className="shrink-0 text-right">
                        {prices[v.id]
                          ? <span className="font-display text-base text-gold">{money(prices[v.id].total)}</span>
                          : pricing
                            ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                            : <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">add route</span>}
                      </div>
                    </button>
                  ))}
                </div>
                <p data-testid="rider-price-hint" className="mt-2 text-xs text-slate-500">
                  {Object.keys(prices).length > 0
                    ? "Worked out on the real driving distance for your route."
                    : "Add your pickup and drop-off — every vehicle then shows its price."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Pickup date</label>
                  <Input data-testid="rider-date" type="date" required value={form.trip_date} onChange={set("trip_date")} className={field} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Pickup time</label>
                  <Input data-testid="rider-time" type="time" required value={form.pickup_time} onChange={set("pickup_time")} className={field} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Passengers</label>
                  <Input data-testid="rider-passengers" type="number" min="1" value={form.passengers} onChange={set("passengers")} className={field} />
                </div>
                {form.pricing_mode === "hourly" && (
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Hours</label>
                    <Input data-testid="rider-hours" type="number" step="0.5" min="1" value={form.hours} onChange={(e) => { set("hours")(e); setQuote(null); }} className={field} />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input data-testid="rider-airline" placeholder="Airline" value={form.airline} onChange={set("airline")} className={field} />
                <Input data-testid="rider-flight" placeholder="Flight #" value={form.flight_number} onChange={set("flight_number")} className={field} />
                <Input data-testid="rider-terminal" placeholder="Terminal" value={form.terminal} onChange={set("terminal")} className={field} />
              </div>

              <button type="button" data-testid="rider-meet-greet"
                onClick={() => reset({ meet_greet: !form.meet_greet })}
                className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors ${
                  form.meet_greet ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/30"
                }`}>
                <Plane className={`h-4 w-4 ${form.meet_greet ? "text-gold" : "text-slate-500"}`} />
                <span className="text-sm text-slate-200">
                  Meet &amp; greet inside the terminal
                  {vehicle?.meet_greet_fee ? <span className="text-slate-500"> — +{money(vehicle.meet_greet_fee)}</span> : ""}
                </span>
                {form.meet_greet && <CheckCircle2 className="ml-auto h-4 w-4 text-gold" />}
              </button>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input data-testid="rider-name" required placeholder="Full name" value={form.customer_name} onChange={set("customer_name")} className={field} />
                <Input data-testid="rider-email" required type="email" placeholder="Email" value={form.customer_email} onChange={set("customer_email")} className={field} />
                <Input data-testid="rider-phone" placeholder="Mobile" value={form.customer_phone} onChange={set("customer_phone")} className={field} />
                <label data-testid="rider-sms-consent-label" className="flex cursor-pointer items-start gap-3 sm:col-span-2">
                  <input data-testid="rider-sms-consent" type="checkbox" checked={form.sms_consent}
                    onChange={(e) => setForm((f) => ({ ...f, sms_consent: e.target.checked }))}
                    className="mt-1 h-4 w-4 accent-gold" />
                  <span className="text-xs leading-relaxed text-slate-400">
                    {SMS_CONSENT_TEXT} See our{" "}
                    <a href="/legal/sms" target="_blank" rel="noreferrer" className="text-gold hover:underline">SMS terms</a>{" "}
                    and <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-gold hover:underline">privacy policy</a>.
                  </span>
                </label>
                {info?.company?.payment_policy?.auto_charge_balance && info?.company?.online_payments_ready
                  && (info?.company?.payment_processor || "stripe") === "stripe" && (
                  <label data-testid="rider-save-card-label" className="flex cursor-pointer items-start gap-3 sm:col-span-3">
                    <input data-testid="rider-save-card" type="checkbox" checked={form.save_card}
                      onChange={(e) => setForm((f) => ({ ...f, save_card: e.target.checked }))}
                      className="mt-1 h-4 w-4 accent-gold" />
                    <span className="text-xs leading-relaxed text-slate-400">
                      Keep my card on file and settle the rest of my fare after the trip — the
                      balance is charged automatically about 30 minutes after we arrive. You can
                      still add a gratuity afterwards.
                    </span>
                  </label>
                )}
              </div>

              <Textarea data-testid="rider-notes" rows={2} placeholder="Anything your chauffeur should know" value={form.notes} onChange={set("notes")}
                className="border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />

              <Button type="button" data-testid="rider-quote-btn" disabled={busy === "quote"} onClick={getQuote} variant="outline"
                className="h-12 w-full border-gold/40 bg-transparent text-gold hover:bg-gold/10">
                {busy === "quote" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Calculator className="mr-2 h-4 w-4" /> Recalculate my price</>)}
              </Button>
              {quote && (
                <>
                  <QuoteBreakdown quote={quote} miles={miles} testId="rider-quote" label="Your price" simple />
                  <Button type="button" data-testid="rider-email-quote" variant="outline"
                    disabled={busy === "email-quote"} onClick={emailQuote}
                    className="h-11 w-full border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                    {busy === "email-quote"
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : (<><Mail className="mr-2 h-4 w-4" /> Email me this quote</>)}
                  </Button>
                  {quoteNotice && <p data-testid="rider-quote-sent" className="text-sm text-emerald-400">{quoteNotice}</p>}
                </>
              )}
              {error && <p data-testid="rider-error" className="text-sm text-red-400">{error}</p>}
              <Button data-testid="rider-submit" type="submit" disabled={busy === "submit"} className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
                {busy === "submit" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Book this ride"}
              </Button>
            </form>
          )}

          <div className="mt-8 border-t border-ink-600 pt-6">
            {rider ? (
              <div data-testid="rider-signed-in" className="rounded-xl border border-ink-600 bg-ink-800/60 p-5">
                <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Signed in</p>
                <p className="mt-2 text-sm text-slate-300">{rider.email}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button data-testid="rider-my-trips" onClick={() => navigate(ridePath(handle, `trips/${rider.token}`))}
                    className="bg-gold text-ink-900 hover:bg-gold-light">My trips & saved addresses</Button>
                  <Button data-testid="rider-sign-out" variant="outline" onClick={signOut}
                    className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">Sign out</Button>
                </div>
              </div>
            ) : (
              <RiderAuthPanel companyId={cid} onSignedIn={signIn} />
            )}
          </div>

          <p className="mt-6 text-[11px] leading-relaxed text-slate-500">
            <strong className="text-slate-400">Transportation Service Disclaimer</strong> — This transportation service
            is provided by {c.name}, the transportation operator identified on this reservation. The software platform
            facilitates communication, scheduling, dispatch and payment technology but does not provide or operate the
            transportation service.
          </p>
          {c.show_powered_by && (
            <p data-testid="rider-powered-by" className="mt-4 text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
          )}
        </div>

        <button data-testid="rider-classic-link" onClick={() => navigate(ridePath(handle, "book"))}
          className="mx-auto mt-6 flex items-center gap-2 font-mono text-[11px] text-slate-600 hover:text-gold">
          <MapPin className="h-3 w-3" /> Classic booking form
        </button>
      </div>
    </div>
  );
}
